import { apiErrorMessage } from '@/utils/apiError'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { accountClosedCode, api, freshAccessToken } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { track } from '@/services/analytics'
import type { Order, OrderSummary, Message, Dispute } from '@/types/api'

function parseMessageList(res: any): Message[] {
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res?.messages)) return res.messages
  return []
}

function computeHasMore(res: any): boolean {
  if (typeof res?.hasMore === 'boolean') return res.hasMore
  if (typeof res?.totalPages === 'number' && typeof res?.page === 'number') {
    return res.page < res.totalPages
  }
  return false
}

function normalizeWsSender(raw: any) {
  if (raw.sender) return raw.sender
  if (!raw.senderID && !raw.senderId) return undefined
  return {
    id: raw.senderID ?? raw.senderId ?? '',
    username: raw.senderUsername ?? raw.username ?? '',
    avatarURL: raw.senderAvatarURL ?? raw.avatarURL,
  }
}

function normalizeWsMessage(raw: any, orderId: string): Message {
  return {
    id: raw.id ?? raw.messageID ?? `ws-${Date.now()}`,
    orderID: raw.orderID ?? orderId,
    sender: normalizeWsSender(raw),
    content: raw.content ?? '',
    attachmentURL: raw.attachmentURL,
    messageType: raw.messageType ?? (raw.attachmentURL ? 'image' : 'text'),
    createdAt: raw.createdAt ?? raw.timestamp ?? new Date().toISOString(),
  }
}

export const useOrdersStore = defineStore('orders', () => {
  // State
  const orders = ref<OrderSummary[]>([])
  const currentOrder = ref<Order | null>(null)
  const messages = ref<Message[]>([])
  const disputes = ref<Dispute[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const ws = ref<WebSocket | null>(null)
  const wsReconnectAttempts = ref(0)
  let wsReconnectTimer: ReturnType<typeof setTimeout> | undefined
  let wsStableTimer: ReturnType<typeof setTimeout> | undefined
  let wsGeneration = 0
  const maxReconnectAttempts = 8
  const wsMaxBackoffMs = 30_000
  const wsStableThresholdMs = 10_000

  // Buying/Selling tabs
  const activeTab = ref<'buying' | 'selling' | 'all'>('all')

  // Computed
  const authStore = useAuthStore()
  const userId = computed(() => authStore.user?.id)
  const buyingOrders = computed(() => orders.value.filter(o => o.buyer.id === userId.value && o.status !== 'cancelled' && o.status !== 'refunded'))
  const sellingOrders = computed(() => orders.value.filter(o => o.seller.id === userId.value && o.status !== 'cancelled' && o.status !== 'refunded'))

  // Actions
  async function fetchOrders(role?: string) {
    if (isLoading.value) return

    isLoading.value = true
    error.value = null

    try {
      orders.value = await api.getOrders(role)
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to load orders')
      if (err.response?.status !== 401) {
        authStore.addToast('error', error.value)
      }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchOrder(id: string) {
    isLoading.value = true
    error.value = null

    try {
      currentOrder.value = await api.getOrder(id)
      return currentOrder.value
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to load order')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function createOrder(listingId: string) {
    isLoading.value = true
    error.value = null

    try {
      const order = await api.createOrder({ listingID: listingId })
      orders.value.unshift(order)
      track('Order Placed', { order_id: order.id, listing_id: listingId, amount_pi: order.amountPi })
      return order
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to create order')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function acceptOrder(id: string) {
    try {
      const order = await api.acceptOrder(id)
      updateOrderInList(order)
      return order
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to accept order')
      throw err
    }
  }

  async function deliverOrder(id: string, payload: { details: string; attachments?: string[] }) {
    try {
      const order = await api.deliverOrder(id, payload)
      updateOrderInList(order)
      return order
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to deliver order')
      throw err
    }
  }

  async function acceptDelivery(id: string) {
    try {
      const order = await api.acceptDelivery(id)
      updateOrderInList(order)
      track('Delivery Accepted', { order_id: order.id, amount_pi: order.amountPi })
      return order
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to accept delivery')
      throw err
    }
  }

  async function cancelOrder(id: string, reason: string) {
    try {
      const order = await api.cancelOrder(id, reason)
      updateOrderInList(order)
      return order
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to cancel order')
      throw err
    }
  }

  async function createDispute(payload: { orderID: string; reason: string; description: string }) {
    try {
      const dispute = await api.createDispute(payload)
      return dispute
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to create dispute')
      throw err
    }
  }

  async function fetchMessages(orderId: string, page = 1, limit = 50) {
    try {
      const res: any = await api.getMessages(orderId, page, limit)
      const rawList = parseMessageList(res)
      if (page === 1) {
        messages.value = rawList
      } else {
        messages.value = [...rawList, ...messages.value]
      }
      const hasMore = computeHasMore(res)
      return {
        ...res,
        data: rawList,
        messages: rawList,
        hasMore,
      }
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to load messages')
      throw err
    }
  }

  async function sendMessage(orderId: string, payload: { content: string; attachmentURL?: string }) {
    try {
      const message = await api.sendMessage(orderId, payload)
      return message
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to send message')
      throw err
    }
  }

  function addMessage(message: Message) {
    const exists = messages.value.some(m => m.id === message.id)
    if (!exists) {
      messages.value.push(message)
    }
  }

  async function fetchDisputes() {
    try {
      disputes.value = await api.getDisputes()
    } catch (err: any) {
      error.value = apiErrorMessage(err, 'Failed to load disputes')
      if (err.response?.status !== 401) {
        authStore.addToast('error', error.value)
      }
    }
  }

  // `onReconnect` fires when a dropped socket comes back, so the caller can
  // backfill messages sent while it was down.
  function connectWebSocket(
    orderId: string,
    onMessage: (message: Message) => void,
    onReconnect?: () => void,
    isReconnect = false,
  ) {
    // Also invalidates an earlier connect still waiting on a token refresh.
    // A reconnect keeps its attempt count: the timer that started it was the
    // only pending one.
    if (!isReconnect) disconnectWebSocket()
    const generation = wsGeneration
    void openWebSocket(orderId, onMessage, onReconnect, isReconnect, generation)
  }

  async function openWebSocket(
    orderId: string,
    onMessage: (message: Message) => void,
    onReconnect: (() => void) | undefined,
    isReconnect: boolean,
    generation: number,
  ) {
    const token = await freshAccessToken()
    // Disconnected or replaced while the token was renewed.
    if (!token || generation !== wsGeneration || ws.value) return
    const defaultUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
    const wsUrl = import.meta.env.VITE_WS_URL || defaultUrl
    ws.value = new WebSocket(`${wsUrl}/api/v1/ws/orders/${orderId}?token=${encodeURIComponent(token)}`)

    ws.value.onopen = () => {
      if (isReconnect) onReconnect?.()
      // Only reset attempts after the connection has been stable for a while.
      // This prevents the counter from resetting when the server crashes
      // immediately after the WebSocket upgrade succeeds.
      clearTimeout(wsStableTimer)
      wsStableTimer = setTimeout(() => {
        wsReconnectAttempts.value = 0
      }, wsStableThresholdMs)
    }

    ws.value.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'message') {
          onMessage(normalizeWsMessage(data.message || data, orderId))
        }
      } catch (err) {
        console.error('Failed to parse WS message:', err)
      }
    }

    ws.value.onclose = (event) => {
      if (generation !== wsGeneration) return
      ws.value = null
      clearTimeout(wsStableTimer)
      // 4003: the account was banned or deleted; reconnecting cannot succeed.
      if (event.code !== accountClosedCode && wsReconnectAttempts.value < maxReconnectAttempts) {
        wsReconnectAttempts.value++
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s… capped at 30s, plus jitter
        const base = Math.min(1000 * Math.pow(2, wsReconnectAttempts.value - 1), wsMaxBackoffMs)
        const jitter = Math.random() * base * 0.3
        const delay = base + jitter
        wsReconnectTimer = setTimeout(() => {
          if (generation === wsGeneration) connectWebSocket(orderId, onMessage, onReconnect, true)
        }, delay)
      }
    }

    ws.value.onerror = (err) => {
      console.error('WebSocket error:', err)
    }
  }

  function disconnectWebSocket() {
    wsGeneration++
    clearTimeout(wsReconnectTimer)
    clearTimeout(wsStableTimer)
    wsReconnectAttempts.value = 0
    if (ws.value) {
      ws.value.onclose = null
      ws.value.close()
      ws.value = null
    }
  }

  function updateOrderInList(order: OrderSummary) {
    const index = orders.value.findIndex(o => o.id === order.id)
    if (index > -1) {
      orders.value[index] = order
    }
    if (currentOrder.value?.id === order.id) {
      currentOrder.value = order as any
    }
  }

  function setActiveTab(tab: 'buying' | 'selling' | 'all') {
    activeTab.value = tab
  }

  function clearCurrentOrder() {
    currentOrder.value = null
  }

  function clearMessages() {
    messages.value = []
  }

  return {
    // State
    orders,
    currentOrder,
    messages,
    disputes,
    isLoading,
    error,
    activeTab,
    // Computed
    buyingOrders,
    sellingOrders,
    // Actions
    fetchOrders,
    fetchOrder,
    createOrder,
    acceptOrder,
    deliverOrder,
    acceptDelivery,
    cancelOrder,
    createDispute,
    fetchMessages,
    sendMessage,
    addMessage,
    fetchDisputes,
    connectWebSocket,
    disconnectWebSocket,
    setActiveTab,
    clearCurrentOrder,
    clearMessages,
  }
})